import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Save, Copy, Check, Code2, MessageSquare, Users } from "lucide-react";
import { toast } from "sonner";
import { adminListInstances, adminCreateInstance, adminUpdateInstance, adminDeleteInstance, adminInstanceLeads } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const input = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none";
const label = "text-xs font-semibold text-slate-600 block mb-1";

const snippetFor = (inst) => {
  const a = [`src="${API_BASE}/api/embed/assistant.js"`, `data-key="${inst.key}"`, `data-name="${(inst.name || "").replace(/"/g, "")}"`, `data-accent="${inst.accent || "#2563EB"}"`];
  if (inst.title) a.push(`data-title="${inst.title.replace(/"/g, "")}"`);
  return `<script ${a.join(" ")} defer></script>`;
};

function UsageChart({ daily = {}, dailyLeads = {}, accent = "#2563EB" }) {
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ key, label: d.toLocaleDateString([], { month: "numeric", day: "numeric" }), chats: daily[key] || 0, leads: dailyLeads[key] || 0 });
  }
  const max = Math.max(1, ...days.map((d) => d.chats));
  const total = days.reduce((s, d) => s + d.chats, 0);
  return (
    <div data-testid="embed-usage-chart">
      <div className="text-sm font-semibold text-slate-700 mb-2">Chats — last 14 days <span className="text-slate-400 font-normal">({total})</span></div>
      <div className="flex items-end gap-1 h-24">
        {days.map((d) => (
          <div key={d.key} className="flex-1 flex flex-col items-center justify-end group relative" title={`${d.label}: ${d.chats} chats, ${d.leads} leads`}>
            <div className="w-full rounded-t transition-all" style={{ height: `${(d.chats / max) * 100}%`, minHeight: d.chats ? 4 : 2, background: d.chats ? accent : "#e2e8f0" }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>{days[0].label}</span><span>{days[days.length - 1].label}</span></div>
    </div>
  );
}

export default function AdminEmbed() {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);      // editing instance object
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => adminListInstances().then((d) => setList(d.instances || [])).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  const openInstance = (inst) => {
    setSel({ ...inst, prompts: inst.prompts || [], domains: inst.domains || [] });
    if (inst.id) adminInstanceLeads(inst.id).then((d) => setLeads(d.leads || [])).catch(() => setLeads([]));
    else setLeads([]);
  };
  const newInstance = () => { setSel({ name: "New Assistant", business: "", role: "assistant", accent: "#2563EB", title: "", greeting: "", prompts: [], knowledge: "", domains: [], active: true }); setLeads([]); };

  const set = (patch) => setSel((s) => ({ ...s, ...patch }));

  const save = async () => {
    setSaving(true);
    const payload = { name: sel.name, business: sel.business, role: sel.role, accent: sel.accent, title: sel.title, greeting: sel.greeting, prompts: sel.prompts, knowledge: sel.knowledge, domains: sel.domains, active: sel.active !== false };
    try {
      const res = sel.id ? await adminUpdateInstance(sel.id, payload) : await adminCreateInstance(payload);
      toast.success(sel.id ? "Saved" : "Assistant created");
      await load(); openInstance(res);
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!sel.id || !window.confirm("Delete this assistant?")) return;
    try { await adminDeleteInstance(sel.id); toast.success("Deleted"); setSel(null); await load(); } catch { toast.error("Delete failed"); }
  };
  const copy = () => { navigator.clipboard.writeText(snippetFor(sel)).then(() => { setCopied(true); toast.success("Snippet copied"); setTimeout(() => setCopied(false), 2000); }); };

  if (!list) return <main className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="animate-spin text-brand" size={26} /></main>;

  return (
    <main className="min-h-screen bg-slate-50 pb-24" data-testid="admin-embed">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center gap-2"><Code2 size={22} className="text-brand" /><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Embed Assistants</h1></div>
        <p className="text-slate-500 mt-1 text-sm">Create branded assistants with custom knowledge, whitelist customer domains, and track chats &amp; leads.</p>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {/* List */}
          <div className="lg:col-span-1">
            <button onClick={newInstance} data-testid="embed-new" className="w-full inline-flex items-center justify-center gap-1.5 bg-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold mb-3"><Plus size={15} /> New assistant</button>
            <div className="space-y-2">
              {list.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No assistants yet.</p>}
              {list.map((inst) => (
                <button key={inst.id} onClick={() => openInstance(inst)} data-testid={`embed-row-${inst.id}`}
                  className={`w-full text-left bg-white border rounded-xl p-3 transition-colors ${sel?.id === inst.id ? "border-brand ring-1 ring-brand/20" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-semibold text-slate-900 truncate">{inst.name}</div>
                  <div className="text-xs text-slate-400 truncate">{inst.business || "—"}</div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><MessageSquare size={11} /> {inst.chats || 0}</span>
                    <span className="inline-flex items-center gap-1"><Users size={11} /> {inst.leads || 0}</span>
                    {inst.active === false && <span className="text-amber-600">paused</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Editor */}
          <div className="lg:col-span-2">
            {!sel ? (
              <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-10 text-center text-slate-400 text-sm">Select an assistant or create a new one.</div>
            ) : (
              <div className="space-y-5" data-testid="embed-editor">
                <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><label className={label}>Assistant name</label><input value={sel.name} onChange={(e) => set({ name: e.target.value })} className={input} data-testid="embed-name" /></div>
                    <div><label className={label}>Business</label><input value={sel.business || ""} onChange={(e) => set({ business: e.target.value })} className={input} /></div>
                    <div><label className={label}>Role</label><input value={sel.role || ""} onChange={(e) => set({ role: e.target.value })} placeholder="e.g. host, advisor" className={input} /></div>
                    <div><label className={label}>Header title</label><input value={sel.title || ""} onChange={(e) => set({ title: e.target.value })} className={input} /></div>
                    <div><label className={label}>Accent color</label><div className="flex items-center gap-2"><input type="color" value={sel.accent || "#2563EB"} onChange={(e) => set({ accent: e.target.value })} className="h-9 w-12 rounded border border-slate-200" /><input value={sel.accent || ""} onChange={(e) => set({ accent: e.target.value })} className={input} /></div></div>
                    <div className="flex items-end"><label className="inline-flex items-center gap-2 text-sm text-slate-600 pb-2"><input type="checkbox" checked={sel.active !== false} onChange={(e) => set({ active: e.target.checked })} className="accent-brand" /> Active</label></div>
                  </div>
                  <div className="mt-3"><label className={label}>Greeting</label><input value={sel.greeting || ""} onChange={(e) => set({ greeting: e.target.value })} placeholder="Hi! How can I help?" className={input} /></div>
                  <div className="mt-3"><label className={label}>Suggested prompts (one per line)</label><textarea value={(sel.prompts || []).join("\n")} onChange={(e) => set({ prompts: e.target.value.split("\n").filter(Boolean) })} rows={3} className={`${input} resize-none`} /></div>
                  <div className="mt-3"><label className={label}>Knowledge base — the facts the assistant answers from</label><textarea value={sel.knowledge || ""} onChange={(e) => set({ knowledge: e.target.value })} rows={6} placeholder="Business hours, services, prices, FAQs, policies…" className={`${input} resize-none`} data-testid="embed-knowledge" /></div>
                  <div className="mt-3"><label className={label}>Allowed domains (one per line — leave blank to allow any)</label><textarea value={(sel.domains || []).join("\n")} onChange={(e) => set({ domains: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} rows={2} placeholder="example.com" className={`${input} resize-none`} data-testid="embed-domains" /></div>
                  <div className="flex items-center gap-2 mt-4">
                    <button onClick={save} disabled={saving} data-testid="embed-save" className="inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
                    {sel.id && <button onClick={remove} className="inline-flex items-center gap-1.5 text-red-500 border border-red-200 rounded-lg px-4 py-2.5 text-sm"><Trash2 size={15} /> Delete</button>}
                  </div>
                </div>

                {sel.id && (
                  <>
                    {/* Snippet */}
                    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
                      <div className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Code2 size={16} className="text-brand" /> Embed snippet</div>
                      <div className="rounded-xl bg-slate-900 text-slate-100 p-4 relative">
                        <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono" data-testid="embed-instance-snippet">{snippetFor(sel)}</pre>
                        <button onClick={copy} className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5">{copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}</button>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">Paste before <code className="bg-slate-100 px-1 rounded">&lt;/body&gt;</code> on the customer's site.</p>
                    </div>

                    {/* Analytics */}
                    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5" data-testid="embed-analytics">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-blue-50 p-4"><div className="text-2xl font-bold text-brand">{sel.chats || 0}</div><div className="text-xs text-slate-500 uppercase tracking-wide mt-1">Chats</div></div>
                        <div className="rounded-xl bg-emerald-50 p-4"><div className="text-2xl font-bold text-emerald-700">{sel.leads || 0}</div><div className="text-xs text-slate-500 uppercase tracking-wide mt-1">Leads captured</div></div>
                      </div>
                      <div className="mt-4">
                        <UsageChart daily={sel.daily} dailyLeads={sel.dailyLeads} accent={sel.accent} />
                      </div>
                      <div className="mt-4">
                        <div className="text-sm font-semibold text-slate-700 mb-2">Recent leads</div>
                        {leads.length === 0 ? <p className="text-sm text-slate-400">No leads captured yet.</p> : (
                          <div className="space-y-2">
                            {leads.slice(0, 8).map((l, i) => (
                              <div key={i} className="text-sm border border-slate-100 rounded-lg px-3 py-2">
                                <span className="font-medium text-slate-800">{l.name || l.email || "Visitor"}</span>
                                {l.email && <span className="text-slate-400"> · {l.email}</span>}
                                {l.phone && <span className="text-slate-400"> · {l.phone}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
